// #68 Due: the one SLA clock (DC-19). The status word and the tier tint are separate: before 50 % the pill is info and
// carries the due phrase (*Due in 2 business days*); then warn *Reminder*, flag *Near-breach*, red *Overdue* (*Past
// Due* for scorecards). The pill leads with a clock icon; the <time> phrase follows it. The number is Active Time;
// Calendar Time is in the tooltip, shown on hover and focus, so the clock is a tab stop. Inside a link (an Action
// Center row) it is `still`: no tab stop and no tooltip, so nothing interactive nests. `ring` is the 44 px header
// form. It never animates. The tab stop is a group named by the pill's word and the <time> phrase (by their ids,
// never a joined string).

import { useId } from "react";
import { Icon, Pill } from "../core";
import { SLA_PILL_LABELS, type Tone } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { toEpoch, type Instant } from "../lib/time";
import { Tooltip } from "../lib/Tooltip";
import { Ring, type RingBand } from "./Ring";

export type DueTier = "due" | "reminder" | "nearBreach" | "overdue" | "pastDue";

export const DUE_TONE: Readonly<Record<DueTier, Tone & RingBand>> = {
  due: "info", reminder: "warn", nearBreach: "flag", overdue: "red", pastDue: "red",
};

const WORD: Readonly<Record<Exclude<DueTier, "due">, string>> = {
  reminder: SLA_PILL_LABELS.reminder,
  nearBreach: SLA_PILL_LABELS.nearBreach,
  overdue: SLA_PILL_LABELS.overdue,
  pastDue: SLA_PILL_LABELS.pastDue,
};

export interface DueProps {
  tier: DueTier;
  /** The due phrase (a filled *Due in {n} business days*, or a date). */
  phrase: string;
  /** The due instant (the <time> element's dateTime). */
  at: Instant;
  /** The tooltip: Calendar Time (e.g. "Calendar Time · 5 days"). */
  calendar?: string | undefined;
  /** Inside a link: no tab stop, no tooltip. */
  still?: boolean | undefined;
  /** The 44 px header form, with the share of the clock used (0–100). */
  ring?: { pct: number } | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function Due({ tier, phrase, at, calendar, still, ring, hs, className }: DueProps) {
  const hot = useHs(hs);
  const base = useId();
  const tone = DUE_TONE[tier];
  const iso = new Date(toEpoch(at)).toISOString();
  const word = tier === "due" ? null : WORD[tier];
  const stop = !still && Boolean(calendar);
  const wordId = stop && word ? `${base}-w` : undefined;
  const timeId = stop ? `${base}-t` : undefined;
  const clock = (
    <span
      className={cx("mc-due", `mc-due-${tone}`, ring && "mc-due-ring", className)}
      tabIndex={stop ? 0 : undefined}
      role={stop ? "group" : undefined}
      aria-labelledby={stop ? [wordId, timeId].filter(Boolean).join(" ") : undefined}
      {...(still ? {} : hot)}
    >
      {ring ? <Ring size={44} pct={ring.pct} band={tone} label={word ?? phrase} /> : null}
      <Pill tone={tone} dot={false}>
        <Icon name="clock" size={14} />
        {word ? wordId ? <span id={wordId}>{word}</span> : word : <time id={timeId} dateTime={iso}>{phrase}</time>}
      </Pill>
      {word ? <time id={timeId} className="mc-due-t" dateTime={iso}>{phrase}</time> : null}
    </span>
  );
  if (still || !calendar) return clock;
  return <Tooltip label={calendar}>{clock}</Tooltip>;
}
